/**
 * NotesListContent Component
 *
 * Inner content for notes list without layout wrapper.
 * Used for embedding within tabbed views or other containers.
 */

import React, { useCallback, useState } from 'react';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Title as TitleIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';

import { DeleteModal } from '../../../components/DeleteModal';
import { type NoteCategory, type SortOrder,useNotesList } from '../hooks/useNotesList';

import { NoteListRow } from './NoteListRow';
import { NotesEmptyState } from './NotesEmptyState';

export interface NotesListContentProps {
  /** Group ID for group notes list (undefined for user notes) */
  groupId?: string;
  /** Title to display */
  title?: string;
  /** Whether to show issuing user name (for group notes) */
  showIssuingUser?: boolean;
  /** Whether to enable date range filters */
  enableDateFilter?: boolean;
}

export const NotesListContent: React.FC<NotesListContentProps> = ({
  groupId,
  title = 'Notes',
  showIssuingUser = false,
  enableDateFilter = true,
}) => {
  const theme = useTheme();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    // Data
    notes,
    isLoading,
    isLoadingFresh,
    totalCount,

    // Pagination
    currentPage,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    setCurrentPage,
    setItemsPerPage,

    // Filters
    category,
    sortOrder,
    searchTerm,
    noteTitleOnly,
    startDate,
    endDate,
    setCategory,
    setSortOrder,
    setSearchTerm,
    setNoteTitleOnly,
    setStartDate,
    setEndDate,

    // Selection
    selectedNoteIds,
    isAllSelected,
    toggleNoteSelection,
    selectAllNotes,

    // Actions
    deleteSelectedNotes,
    navigateToNote,

    // State helpers
    isEmpty,
    isSearchActive,
    hasFirstNote,

    // Feature flags
    hasNoteSigning,
  } = useNotesList({
    groupId,
    enableDateFilter,
  });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [setSearchTerm],
  );

  const handleCategoryChange = useCallback(
    (e: SelectChangeEvent) => {
      setCategory(e.target.value as NoteCategory);
    },
    [setCategory],
  );

  const handleSortOrderChange = useCallback(
    (e: SelectChangeEvent) => {
      setSortOrder(parseInt(e.target.value) as SortOrder);
    },
    [setSortOrder],
  );

  const handleItemsPerPageChange = useCallback(
    (e: SelectChangeEvent) => {
      setItemsPerPage(parseInt(e.target.value));
    },
    [setItemsPerPage],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  const handleClearFilters = useCallback(() => {
    setCategory('all');
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
  }, [setCategory, setSearchTerm, setStartDate, setEndDate]);

  const handleDeleteClick = useCallback(() => {
    if (selectedNoteIds.length > 0) {
      setShowDeleteModal(true);
    }
  }, [selectedNoteIds.length]);

  const handleConfirmDelete = useCallback(async () => {
    await deleteSelectedNotes();
    setShowDeleteModal(false);
  }, [deleteSelectedNotes]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(currentPage - 1);
  }, [setCurrentPage, currentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(currentPage + 1);
  }, [setCurrentPage, currentPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return (
    <>
      {/* Loading indicator for background refresh */}
      {isLoadingFresh && (
        <Box
          sx={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'background.paper',
            padding: '0.5rem 1rem',
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Refreshing...
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        continueText="Delete"
        cancelText="Cancel"
        onCancel={() => setShowDeleteModal(false)}
        onContinue={handleConfirmDelete}
      >
        Are you sure you want to delete {selectedNoteIds.length} note
        {selectedNoteIds.length > 1 ? 's' : ''}? This action cannot be undone.
      </DeleteModal>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              {totalCount.toLocaleString()} {totalCount === 1 ? 'note' : 'notes'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Filters Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Category Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select value={category} onChange={handleCategoryChange}>
            <MenuItem value="all">All Notes</MenuItem>
            <MenuItem value="reviewing">Reviewing</MenuItem>
            <MenuItem value="completed">{hasNoteSigning ? 'Signed' : 'Completed'}</MenuItem>
          </Select>
        </FormControl>

        {/* Sort Order */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select value={sortOrder.toString()} onChange={handleSortOrderChange}>
            <MenuItem value="-1">Newest First</MenuItem>
            <MenuItem value="1">Oldest First</MenuItem>
          </Select>
        </FormControl>

        {/* Items Per Page */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select value={itemsPerPage.toString()} onChange={handleItemsPerPageChange}>
            <MenuItem value="20">20</MenuItem>
            <MenuItem value="50">50</MenuItem>
            <MenuItem value="100">100</MenuItem>
          </Select>
        </FormControl>

        {/* Date Pickers */}
        {enableDateFilter && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue: Dayjs | null) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue: Dayjs | null) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 },
                },
              }}
            />
          </LocalizationProvider>
        )}
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search notes..."
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.palette.grey[400] }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip
                  title={noteTitleOnly ? 'Searching titles only' : 'Searching all content'}
                >
                  <IconButton
                    size="small"
                    onClick={() => setNoteTitleOnly(!noteTitleOnly)}
                    color={noteTitleOnly ? 'primary' : 'default'}
                  >
                    <TitleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'background.paper',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.grey[400],
              },
            },
          },
        }}
      />

      {/* Bulk Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button variant="outlined" size="small" onClick={selectAllNotes} sx={{ textTransform: 'none' }}>
          {isAllSelected ? 'Deselect All' : 'Select All'}
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
          disabled={selectedNoteIds.length === 0}
          sx={{ textTransform: 'none' }}
        >
          Delete ({selectedNoteIds.length})
        </Button>
      </Box>

      {/* List Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Loading state */}
        {isLoading && notes.length === 0 ? (
          <Box>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 3,
                  py: 2.5,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="70%" height={22} sx={{ mb: 0.5 }} />
                  <Skeleton width="40%" height={18} sx={{ mb: 0.75 }} />
                  <Skeleton width="90%" height={16} />
                  <Skeleton width="60%" height={16} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Skeleton width={60} height={18} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          <NotesEmptyState
            isSearchActive={isSearchActive}
            searchTerm={searchTerm}
            hasGeneratedFirstNote={hasFirstNote}
            onClearSearch={handleClearSearch}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <>
            {/* List */}
            <Box sx={{ position: 'relative' }}>
              {isLoading && notes.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {notes.map(note => (
                <NoteListRow
                  key={note.note_id}
                  note={note}
                  onSelect={navigateToNote}
                  isSelected={selectedNoteIds.includes(note.note_id)}
                  onToggleSelection={toggleNoteSelection}
                  showCheckbox={true}
                  showIssuingUser={showIssuingUser}
                  hasNoteSigning={hasNoteSigning}
                />
              ))}
            </Box>

            {/* Pagination */}
            {totalCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 3,
                  py: 1.5,
                  borderTop: `1px solid ${theme.palette.grey[100]}`,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Page {currentPage} of {totalPages}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={handlePreviousPage}
                    disabled={!hasPreviousPage || isLoading}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleNextPage}
                    disabled={!hasNextPage || isLoading}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  );
};
